import { useMemo, useState } from "react";
import { TenstreetApplicant } from "src/types/tenstreet-applicant";
import { SavedExam } from "../../types/exam";
import { Question } from "../../types/question";
import { Section } from "../../types/section";
import { getFullName } from "../../utils/get-full-name";
import { ExamFooter } from "./exam-footer";
import { ExamHeader } from "./exam-header";
import { ExamSectionContent } from "./exam-section-content";
import { ExamSectionList } from "./exam-section-list";

export function Exam({
    exam,
    driver,
    onExamUpdate,
    onExit,
    isExamCompleted,
    canExamBeCompleted,
}: {
    exam: SavedExam;
    driver: TenstreetApplicant;
    onExamUpdate: (updatedExam: SavedExam) => void;
    onExit: () => void;
    isExamCompleted: boolean;
    canExamBeCompleted: boolean;
}) {
    const [currentSectionId, setCurrentSectionId] = useState<number>(
        exam.sections[0].sectionID,
    );

    const currentSection =
        exam.sections.find((s) => s.sectionID === currentSectionId) ||
        exam.sections[0];

    const isEveryQuestionAnswered = useMemo(
        () => currentSection.questions.every((q) => q.answer),
        [currentSection],
    );

    function handleQuestionSelection(updatedQuestion: Question) {
        // Update the section's questions array by replacing the modified question
        const updatedQuestions = currentSection.questions.map((q) =>
            q.questionID === updatedQuestion.questionID ? updatedQuestion : q,
        );

        const updatedSection: Section = {
            ...currentSection,
            questions: updatedQuestions,
            status: "in_progress",
        };

        const updatedSections = exam.sections.map((section) =>
            section.sectionID === updatedSection.sectionID
                ? updatedSection
                : section,
        );

        const updatedExam = {
            ...exam,
            sections: updatedSections,
        };

        onExamUpdate(updatedExam);
    }

    return (
        <div className="w-full max-w-5xl bg-white border border-base-300 rounded-lg shadow-md overflow-hidden">
            <ExamHeader
                driver={{
                    name: getFullName(
                        driver.firstName,
                        driver.middleName,
                        driver.lastName,
                    ),
                    licenseNumber: driver.licenseNumber,
                }}
                examType={{
                    name: exam.examType,
                }}
                sections={exam.sections}
            />

            <div className="flex h-[calc(100vh-200px)]">
                <div className="w-48 border-r border-base-300 bg-gray-50 px-2 divide-y">
                    <ExamSectionList
                        sections={exam.sections}
                        currentSectionId={currentSectionId}
                        setCurrentSectionId={setCurrentSectionId}
                    />

                    <div className="py-2">
                        <fieldset className="fieldset w-full">
                            <legend className="fieldset-legend pt-0">
                                Power Unit
                            </legend>
                            <select
                                className="select select-bordered select-sm w-full"
                                value={exam.typeOfPowerUnit ?? ""}
                                onChange={(e) =>
                                    onExamUpdate({
                                        ...exam,
                                        typeOfPowerUnit: e.target.value,
                                    })
                                }
                                disabled={isExamCompleted}
                            >
                                <option value="">Select power unit</option>
                                <option value="Automatic">Automatic</option>
                                <option value="Manual">Manual</option>
                            </select>
                        </fieldset>

                        <fieldset className="fieldset w-full">
                            <legend className="fieldset-legend">
                                Type of Trailer
                            </legend>
                            <select
                                className="select select-bordered select-sm w-full"
                                value={exam.typeOfTrailer ?? ""}
                                onChange={(e) =>
                                    onExamUpdate({
                                        ...exam,
                                        typeOfTrailer: e.target.value,
                                    })
                                }
                                disabled={isExamCompleted}
                            >
                                <option value="">Select trailer type</option>
                                <option value="53' dry van">53' dry van</option>
                            </select>
                        </fieldset>

                        <fieldset className="fieldset w-full">
                            <legend className="fieldset-legend">
                                Miles Driven
                            </legend>
                            <input
                                type="number"
                                className="input input-bordered input-sm w-full"
                                value={exam.milesDriven ?? ""}
                                onChange={(e) =>
                                    onExamUpdate({
                                        ...exam,
                                        milesDriven: Number(e.target.value),
                                    })
                                }
                                disabled={isExamCompleted}
                            />
                        </fieldset>
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    <ExamSectionContent
                        exam={exam}
                        onExamUpdate={onExamUpdate}
                        section={currentSection}
                        onQuestionSelection={handleQuestionSelection}
                        isEveryQuestionAnswered={isEveryQuestionAnswered}
                        sectionStatus={currentSection.status}
                        driver={{
                            name: getFullName(
                                driver.firstName,
                                driver.middleName,
                                driver.lastName,
                            ),
                        }}
                        isExamCompleted={isExamCompleted}
                    />
                </div>
            </div>

            <ExamFooter
                isExamCompleted={isExamCompleted}
                canExamBeCompleted={canExamBeCompleted}
                totalSections={exam.sections.length}
                onCompleteExam={onExamUpdate}
                onExit={onExit}
                driver={driver}
                exam={exam}
            />
        </div>
    );
}
